import * as React from 'react';

import {DisposalForm} from 'features/search/filters/enums';
import {PremisesType} from 'components/pages/PremisesStartPage/types';
import {renderInhabitDate} from 'features/services/utils/date-helpers';
import {getDisposalFormName} from 'features/services/utils/name-helpers';
import {getFormattedMessage} from 'features/services/utils/language-helper';
import {isPremiseSoldOrRented, translatePremiseEnum} from 'components/pages/PremisesStartPage/helpers';
import {getLocalizedKvm, getLocalizedPrice} from 'features/services/utils/env-helper';

import './style.scss';

import { Facts } from '.';
import {QuickFactsItem} from './item';

interface Props {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- viewModel varies by page type
    viewModel: any;
    addressString: string;
}

export const PremisesQuickFacts: React.FunctionComponent<Props> = ({
    viewModel, addressString
}) => {
    
    const getPremisesTypes = (type) => {
        let typeString = '';

        type.map((x, index) => {
            typeString += translatePremiseEnum((x as PremisesType));
            if(index !== type.length - 1) typeString += ', '
        });
        return typeString;
    };
    
    const getPremisesArea = (minArea?: number, maxArea?: number) => {
        if (!maxArea) return getLocalizedKvm(minArea);
        return getLocalizedKvm(`${minArea} - ${maxArea}`);
    };

    const facts : Facts[] = [
        {key: 'address', devCounter: '01',//(10)  
            label: getFormattedMessage('jm.components.QuickFacts.address'), 
            fact: addressString, 
            condition: addressString 
        },
        {key: 'disposalForm', devCounter: '02',//(12)  
            label: getFormattedMessage('jm.components.QuickFacts.disposalForm'), 
            fact: getDisposalFormName(viewModel.disposalForm as DisposalForm), 
            condition: viewModel.disposalForm 
        },
        {key: 'elevator', devCounter: '03',//(13)  
            label: getFormattedMessage('jm.components.QuickFacts.elevator'), 
            fact: getFormattedMessage('jm.components.QuickFacts.yes'), 
            condition: viewModel.elevator 
        },
        {key: 'fee', devCounter: '04',//(5/6)
            label: !viewModel.disposalForm || viewModel.disposalForm === DisposalForm.TENANCY
                ? getFormattedMessage("jm.components.QuickFacts.premisesFee")
                : getFormattedMessage("jm.components.QuickFacts.premisesPrice"),
            fact: viewModel.fee
                ? getLocalizedPrice(viewModel.fee)
                : getFormattedMessage("jm.components.QuickFacts.feeContact"),
            condition: !isPremiseSoldOrRented(viewModel.premisesStatus)
        },
        {key: 'floor', devCounter: '05',//(03)  
            label: getFormattedMessage('jm.components.QuickFacts.floor'), 
            fact: viewModel.maxFloor
                ? `${viewModel.floor}` + ' / ' +  `${viewModel.maxFloor}`
                : `${viewModel.floor}`,
            condition: viewModel.floor  
        },
        {key: 'inhabitDate', devCounter: '06',//(04)  
            label: getFormattedMessage('jm.components.QuickFacts.inhabitDate'), 
            fact: renderInhabitDate(viewModel.inhabitDate, undefined, !viewModel.preliminaryInhabitDate, false, true), 
            condition: viewModel.inhabitDate 
        },
        {key: 'loanPrice', devCounter: '07',//(08)  
            label: getFormattedMessage('jm.components.QuickFacts.premisesLoanPrice'), 
            fact: getLocalizedPrice(viewModel.loanPrice), 
            condition: viewModel.loanPrice 
        },
        {key: 'minMaxArea', devCounter: '08',//(02)  
            label: getFormattedMessage('jm.components.QuickFacts.premisesArea'), 
            fact: getPremisesArea(viewModel.area, viewModel.maxArea), 
            condition: viewModel.area 
        },
        {key: 'price', devCounter: '09',//(07)  
            label: getFormattedMessage('jm.components.QuickFacts.premisesPrice'), 
            fact: getLocalizedPrice(viewModel.price), 
            condition: viewModel.price 
        },
        {key: 'objectNumber', devCounter: '10',//(11)  
            label: getFormattedMessage('jm.components.QuickFacts.premisesNumber'), 
            fact: viewModel.objectNumber, 
            condition: viewModel.objectNumber 
        },
        {key: 'totalPrice', devCounter: '11',//(09)  
            label: getFormattedMessage('jm.components.QuickFacts.premisesTotalPrice'), 
            fact: getLocalizedPrice(viewModel.totalPrice), 
            condition: viewModel.totalPrice 
        },
        {key: 'type', devCounter: '12',//(01)  
            label: getFormattedMessage('jm.components.QuickFacts.premisesType'), 
            fact: getPremisesTypes(viewModel.premisesTags.map(x => x.id)), 
            condition: viewModel.premisesTags.length > 0 
        }
    ];

    const renderFactsByOrder = (factOrder: string[]) => {
        let _factIndex = 0;
        return factOrder.map((key, index) => {
            const fact = facts.find(f => f.key === key);
            if (!fact || !fact.condition) return null;
            _factIndex++;

            return (
                <QuickFactsItem
                    key={fact.key}
                    index={index.toString()}
                    devCounter={fact.devCounter}
                    itemLabel={fact.label}
                    itemFact={fact.fact}
                />
            );
        })
        .filter(Boolean);
    };

    const order = [
        'type',
        'minMaxArea',
        'floor',
        'inhabitDate',
        'fee',
        'price',
        'loanPrice',
        'totalPrice',
        'address',
        'objectNumber',
        'disposalForm',
        'elevator',
    ];

    return (
        <dl className="quick-facts__list">
            {renderFactsByOrder(order)}
        </dl>
    )
};